<template>
  <!--
    A placeholder in the shape of what is coming.

    Not a spinner: a spinner says "wait" and nothing else, while a skeleton
    already shows how many rows there will be and where they sit, so the page
    stops jumping when the data lands. That is the whole point — the layout is
    final before the content arrives.

    aria-hidden with a live region above it: a screen reader should hear
    "loading", once, not a description of grey boxes.

    Para una pantalla entera no se compone a mano con este: UiSkeletonList,
    UiSkeletonFile, UiSkeletonCards y UiSkeletonForm ya tienen la forma de la
    lista, la ficha, las cifras y el formulario del ERP, y UiLoadState decide
    cuándo enseñarlos (ver components/SKELETONS.md).
  -->
  <div
    v-if="lines > 1"
    class="skeleton-group"
    :style="{ gap }"
    aria-hidden="true"
  >
    <span
      v-for="line in lines"
      :key="line"
      class="skeleton"
      :class="`skeleton--${variant}`"
      :style="lineStyle(line)"
    />
  </div>

  <span
    v-else
    class="skeleton"
    :class="`skeleton--${variant}`"
    :style="lineStyle(1)"
    aria-hidden="true"
  />
</template>

<script setup>
const props = defineProps({
  // text | title | block | circle | button | field | chip
  variant: { type: String, default: 'text' },
  lines: { type: Number, default: 1 },
  width: String,
  height: String,
  gap: { type: String, default: 'var(--space-2)' },
  // The last line of a paragraph is rarely full width; faking that keeps it
  // from reading as a solid grey slab.
  ragged: { type: Boolean, default: true },
})

function lineStyle(line) {
  const isLast = line === props.lines && props.lines > 1
  return {
    ...(props.width ? { width: props.width } : {}),
    ...(props.height ? { height: props.height } : {}),
    ...(isLast && props.ragged && props.variant === 'text' ? { width: '62%' } : {}),
  }
}
</script>

<style scoped>
.skeleton-group { display: flex; flex-direction: column; }

/* Los dos colores son del tema (tokens.css, `--skeleton-base` y
   `--skeleton-shine`): en oscuro el brillo es apenas un velo, o el barrido
   parpadea como un fluorescente. Lento y suave: un brillo rápido se lee como
   «algo va mal», no como «ya llega». */
.skeleton {
  display: block;
  flex: none;
  max-width: 100%;
  border-radius: var(--radius-sm);
  background-color: var(--skeleton-base);
  background-image: linear-gradient(
    100deg,
    transparent 20%,
    var(--skeleton-shine) 50%,
    transparent 80%
  );
  background-size: 200% 100%;
  background-repeat: no-repeat;
  animation: skeleton-sweep 1.6s ease-in-out infinite;
}

.skeleton--text   { height: 0.9em; width: 100%; }
.skeleton--title  { height: 1.4em; width: 40%; border-radius: var(--radius); }
.skeleton--block  { height: 100px; width: 100%; border-radius: var(--radius); }
.skeleton--button { height: 36px; width: 110px; border-radius: var(--radius); }
.skeleton--circle { height: 40px; width: 40px; border-radius: 50%; }
/* La píldora de estado (UiBadge): 24 px de alto y redonda del todo. */
.skeleton--chip   { height: 24px; width: 76px; border-radius: var(--radius-full); }

/* La caja de un campo (UiInput, UiSelect): 38 px, y no la de un botón. */
.skeleton--field  { height: 38px; width: 100%; border-radius: var(--radius); }

/* Con el dedo, un botón mide 42 (UiButton, tamaño md) y un campo 44
   (UiInput), y el hueco que los espera también: si no, la pantalla crece al
   llegar los datos. */
@media (pointer: coarse) {
  .skeleton--button { height: 42px; }
  .skeleton--field  { height: 44px; }
}

@keyframes skeleton-sweep {
  from { background-position: 150% 0; }
  to   { background-position: -50% 0; }
}

/* Sin movimiento, sin barrido: queda la forma gris quieta, que ya dice «aún
   no». La regla global de tokens.css acorta las animaciones a casi nada, pero
   eso deja el degradado congelado a media pasada: se quita entero. */
@media (prefers-reduced-motion: reduce) {
  .skeleton { background-image: none; animation: none; }
}
</style>
